import { jsx as _jsx } from "react/jsx-runtime";
import { NotificationType } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { useContext } from 'react';
import { useOccConfig } from '../../../context/occ/hooks/use-occ-config';
import { Notification } from '../../../subcomponents/notification/notification';
import { extractLastDigitsFromMaskedCardNumber } from '../../../utils/extract-last-digits-from-masked-card-number';
import { getIdForComponentNotification } from '../../../utils/get-id-for-component-notification';
import { MemberPaymentCardWalletContextInstance } from '../types/context';
import { MemberPaymentCardWalletInternalAction } from '../types/internal-action';
export var MemberPaymentCardWalletNotificationType;
(function (MemberPaymentCardWalletNotificationType) {
    MemberPaymentCardWalletNotificationType["ExpiringPrimaryCardWarning"] = "ExpiringPrimaryCardWarning";
    MemberPaymentCardWalletNotificationType["ExpiredPrimaryCardWarning"] = "ExpiredPrimaryCardWarning";
    MemberPaymentCardWalletNotificationType["FrozenDueToFailedPayments"] = "FrozenDueToFailedPayments";
    MemberPaymentCardWalletNotificationType["FrozenDueToExpiredPrimary"] = "FrozenDueToExpiredPrimary";
    MemberPaymentCardWalletNotificationType["FailedLastPayment"] = "FailedLastPayment";
    MemberPaymentCardWalletNotificationType["FailedLastPaymentDueToExpiredPrimary"] = "FailedLastPaymentDueToExpiredPrimary";
    MemberPaymentCardWalletNotificationType["ThirdPartyPaymentProviderPhasingOut"] = "ThirdPartyPaymentProviderPhasingOut";
    MemberPaymentCardWalletNotificationType["UnableToChangePaymentProviderToVista"] = "UnableToChangePaymentProviderToVista";
})(MemberPaymentCardWalletNotificationType || (MemberPaymentCardWalletNotificationType = {}));
const b = bemClassBuilder('v-member-payment-card-wallet-notification');
export const MemberPaymentCardWalletNotification = observer(props => {
    const { member: { subscriptions: subscriptionsConfig } } = useOccConfig();
    const { messageType, primaryCard, onFrozenMessageClicked, onChangePaymentProviderToVistaClicked } = props;
    const { displayText, config } = useContext(MemberPaymentCardWalletContextInstance);
    const message = getNotificationMessage(messageType, displayText);
    const type = getNotificationType(messageType);
    if (messageType === MemberPaymentCardWalletNotificationType.ThirdPartyPaymentProviderPhasingOut &&
        !subscriptionsConfig.showThirdPartyPaymentProviderWarning) {
        return null;
    }
    const cardLastFourDigits = extractLastDigitsFromMaskedCardNumber(primaryCard === null || primaryCard === void 0 ? void 0 : primaryCard.cardDetails.maskedCardNumber, 4);
    return (_jsx("div", Object.assign({ className: b.block() }, { children: _jsx(Notification, { id: getIdForComponentNotification('member-payment-card-wallet', type, messageType), type: type, messages: [message], internalActions: [
                {
                    name: MemberPaymentCardWalletInternalAction.ReactivateMemberSubscription,
                    action: onFrozenMessageClicked
                },
                {
                    name: MemberPaymentCardWalletInternalAction.ChangePaymentProviderToVista,
                    action: onChangePaymentProviderToVistaClicked
                }
            ], templateParameters: { cardLastFourDigits }, displayInline: config.notification.displayInline }) })));
});
MemberPaymentCardWalletNotification.displayName = 'MemberPaymentCardWalletNotification';
const getNotificationMessage = (messageType, displayText) => {
    switch (messageType) {
        case MemberPaymentCardWalletNotificationType.ExpiringPrimaryCardWarning:
            return displayText.primaryCardExpiringSoonWarningMessage;
        case MemberPaymentCardWalletNotificationType.ExpiredPrimaryCardWarning:
            return displayText.primaryCardExpiredWarningMessage;
        case MemberPaymentCardWalletNotificationType.FrozenDueToFailedPayments:
            return displayText.subscriptionFrozenFailedPaymentErrorMessage;
        case MemberPaymentCardWalletNotificationType.FrozenDueToExpiredPrimary:
            return displayText.subscriptionFrozenExpiredPrimaryCardErrorMessage;
        case MemberPaymentCardWalletNotificationType.FailedLastPayment:
            return displayText.lastPaymentFailedErrorMessage;
        case MemberPaymentCardWalletNotificationType.FailedLastPaymentDueToExpiredPrimary:
            return displayText.lastPaymentFailedExpiredPrimaryCardErrorMessage;
        case MemberPaymentCardWalletNotificationType.ThirdPartyPaymentProviderPhasingOut:
            return displayText.thirdPartyPaymentProviderPhasingOutWarningMessage;
        case MemberPaymentCardWalletNotificationType.UnableToChangePaymentProviderToVista:
            return displayText.hasPrimaryCardAndUsingThirdPartyPaymentProviderErrorMessage;
        default:
            return;
    }
};
const getNotificationType = (messageType) => {
    switch (messageType) {
        case MemberPaymentCardWalletNotificationType.ExpiringPrimaryCardWarning:
        case MemberPaymentCardWalletNotificationType.ExpiredPrimaryCardWarning:
        case MemberPaymentCardWalletNotificationType.ThirdPartyPaymentProviderPhasingOut:
            return NotificationType.Warning;
        default:
            return NotificationType.Error;
    }
};
